import MarkdownIt from 'markdown-it'
import { pdfEmbedMarkdown, replacePdfMarkdown } from './pdfEmbed.js'

/**
 * The HTML <-> Markdown pair used by TiptapEditor.vue.
 *
 * Extracted from the component so it can be unit-tested: it lived inside a
 * 3.4k-line SFC, which is how `<ol>` shipped a replacement string (`$1`) passed
 * to a replacer *function* — where `$1` is emitted literally, not interpolated —
 * and silently ate the text of every pasted numbered list. See
 * `working-material/plans/pasted-ordered-list-dollar-one.md`.
 *
 * NOTE: this is deliberately NOT `useMarkdownConverter` (Turndown). This pair
 * preserves the editor's custom markup verbatim — drawings, PDF embeds, colored
 * spans, `<img class="editor-image">`, YouTube embeds — which Turndown drops.
 */

const toPdfMarker = (match, src, width, height) =>
  `\n${pdfEmbedMarkdown(src, width, height)}\n\n`

/**
 * Flatten one `<li>`'s inner HTML to a single markdown line.
 * Tiptap wraps list item content in `<p>`, and the paragraph rule further down
 * htmlToMarkdown appends `\n\n` — leaving those in place would split every item
 * across blank lines and turn each one into its own list.
 */
const listItemMarkdown = (item) => item
  .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
  .replace(/<\/?p[^>]*>/gi, '')
  .replace(/\s*\n\s*/g, ' ')
  .trim()

/**
 * Convert the inner HTML of a `<ul>`/`<ol>` to markdown list lines.
 * @param {string} content - inner HTML of the list element
 * @param {(index: number) => string} marker - `- ` / `1. ` prefix for item N
 */
const listMarkdown = (content, marker) => {
  let index = 0
  return content
    .trim()
    // markdown-it pretty-prints one item per line. That whitespace sits BETWEEN
    // the markdown lines we emit, and a blank line inside a list ends it — so
    // each item would come back as a list of its own.
    .replace(/<\/li>\s+<li/gi, '</li><li')
    .replace(
      /<li[^>]*>(.*?)<\/li>/gis,
      (match, item) => `${marker(index++)}${listItemMarkdown(item)}\n`
    )
}

/**
 * Convert editor HTML to the markdown we persist.
 * @param {string} html - `editor.getHTML()` output
 * @returns {string} markdown
 */
export const htmlToMarkdown = (html) => {
  if (!html || html.trim() === '') return ''

  const result = html
    // Convert headings
    .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
    .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
    .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
    // Convert bold and italic
    .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
    .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
    .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
    // Convert underline (keep as HTML since Markdown doesn't support it)
    .replace(/<u[^>]*>(.*?)<\/u>/gi, '<u>$1</u>')
    // Convert strikethrough
    .replace(/<s[^>]*>(.*?)<\/s>/gi, '~~$1~~')
    .replace(/<del[^>]*>(.*?)<\/del>/gi, '~~$1~~')
    // Convert code
    .replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`')
    // Convert code blocks
    .replace(/<pre[^>]*><code[^>]*>(.*?)<\/code><\/pre>/gis, '\n```\n$1\n```\n\n')
    // Convert blockquotes
    .replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gis, '\n> $1\n\n')
    // Convert links
    .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
    // Convert unordered lists. The item text MUST come from the replacer's
    // capture argument: `$1` inside a function's return value is literal text,
    // never the capture group — that is exactly how the ordered branch below
    // used to persist `1. $1` for every pasted numbered list.
    .replace(/<ul[^>]*>(.*?)<\/ul>/gis, (match, content) =>
      listMarkdown(content, () => '- ') + '\n'
    )
    // Convert ordered lists
    .replace(/<ol[^>]*>(.*?)<\/ol>/gis, (match, content) =>
      listMarkdown(content, (index) => `${index + 1}. `) + '\n'
    )
    // Convert horizontal rules
    .replace(/<hr[^>]*\/?>/gi, '\n---\n\n')
    // Convert PDF embeds. The wrapper's data-* attributes are the primary
    // source; the second/third patterns recover the URL from the frame
    // itself (current <iframe> markup and pre-CSP-fix <object> markup).
    // The marker itself is formatted by pdfEmbedMarkdown so the syntax has
    // exactly one definition (utils/pdfEmbed.js).
    .replace(/<div[^>]*data-pdf-embed[^>]*data-src="([^"]*)"[^>]*data-width="([^"]*)"[^>]*data-height="([^"]*)"[^>]*>.*?<\/div>/gis, toPdfMarker)
    .replace(/<div[^>]*data-pdf-embed[^>]*>.*?<iframe[^>]*src="([^"]*)"[^>]*width="([^"]*)"[^>]*height="([^"]*)"[^>]*>.*?<\/div>/gis, toPdfMarker)
    .replace(/<div[^>]*data-pdf-embed[^>]*>.*?<object[^>]*data="([^"]*)"[^>]*width="([^"]*)"[^>]*height="([^"]*)"[^>]*>.*?<\/div>/gis, toPdfMarker)
    // Convert drawings - extract the lines data from data-type="drawing"
    .replace(/<div[^>]*data-type="drawing"[^>]*data-lines=["']([^"']*)["'][^>]*>.*?<\/div>/gis, (match, linesJson) => {
      try {
        console.log('🎨 Converting drawing from HTML to Markdown')
        console.log('🎨 Raw lines JSON attr:', linesJson.substring(0, 100))

        // Decode HTML entities and parse JSON
        const decodedJson = linesJson.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
        const lines = JSON.parse(decodedJson)

        console.log('🎨 Successfully parsed', lines.length, 'strokes for markdown')

        return `\n\n[DRAWING]${JSON.stringify(lines)}\n\n`
      } catch (e) {
        console.error('❌ Error converting drawing to markdown:', e)
        console.error('❌ Raw attr:', linesJson.substring(0, 200))
        return '\n\n[DRAWING][]\n\n'
      }
    })
    // Remove paragraphs but keep content (except for colored text)
    .replace(/<p[^>]*>((?:(?!<span[^>]*style[^>]*color).)*?)<\/p>/gi, '$1\n\n')
    // Keep colored spans as HTML
    .replace(/<p[^>]*>(<span[^>]*style[^>]*color[^>]*>.*?<\/span>)<\/p>/gi, '$1\n\n')
    // Clean up extra whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  console.log('HTML to Markdown conversion:', {
    input: html,
    output: result
  })
  return result
}

/**
 * Convert persisted markdown back to HTML for the editor.
 * @param {string} markdown - stored card/note markdown
 * @returns {string} HTML for `editor.commands.setContent`
 */
export const markdownToHtml = (markdown) => {
  console.log('🔄 markdownToHtml called with:', {
    markdown,
    type: typeof markdown,
    length: markdown?.length || 0,
    firstChars: markdown?.substring(0, 100) || 'empty'
  })

  if (!markdown || markdown.trim() === '') {
    console.log('❌ Empty markdown, returning empty string')
    return ''
  }

  // Direct conversion approach - skip markdown-it for special content types
  let html = markdown

  // Handle PDF patterns (markup owned by utils/pdfEmbed.js — an <iframe>,
  // because our CSP's `object-src 'none'` blocks <object> plugin loads)
  html = replacePdfMarkdown(html)

  // Handle drawing patterns - match the entire JSON array
  console.log('🔍 Looking for Drawing patterns in markdown')
  console.log('🔍 Markdown sample:', html.substring(0, 200))

  // Match [DRAWING] followed by a JSON array
  // This regex finds [DRAWING] and captures everything until we find a closing bracket
  // that's not part of a nested structure
  const drawingMatches = html.match(/\[DRAWING\](\[[^\n]*\])/g)

  if (drawingMatches && drawingMatches.length > 0) {
    console.log('🎯 Found', drawingMatches.length, 'drawing(s)')

    drawingMatches.forEach((match, index) => {
      console.log(`🔄 Processing drawing ${index + 1}:`, match.substring(0, 100) + '...')

      // Extract the JSON part (everything after [DRAWING])
      const jsonPart = match.substring('[DRAWING]'.length)

      try {
        const lines = JSON.parse(jsonPart)
        console.log('✅ Successfully parsed', lines.length, 'strokes')

        // Escape the JSON for HTML attribute
        const escapedJson = jsonPart.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
        const drawingHtml = `<div data-type="drawing" data-lines="${escapedJson}"></div>`

        // Replace this occurrence in the HTML
        html = html.replace(match, drawingHtml)
        console.log('✅ Replaced drawing in HTML')
      } catch (e) {
        console.error('❌ Error parsing drawing JSON:', e)
        console.error('❌ JSON part:', jsonPart.substring(0, 100))
        // Replace with empty drawing
        html = html.replace(match, '<div data-type="drawing" data-lines="[]"></div>')
      }
    })
  } else {
    console.log('ℹ️ No drawings found in markdown')
  }

  // Now process the rest with markdown-it
  const md = new MarkdownIt({
    html: true, // Allow HTML tags in markdown
    linkify: true, // Auto-convert URLs to links
    typographer: true // Enable smart quotes and other typography
  })

  const finalHtml = md.render(html)
  console.log('✅ Final processed HTML:', finalHtml)

  return finalHtml.trim()
}
